import {
  TranscriptData,
  TranscriptSentence,
} from "@/shared/interfaces/Transcript";
import { MIN_SAMPLE_WORDS } from "../constants";

/** One representative sentence chosen to help identify a speaker. */
export interface SpeakerSampleSentence {
  text: string;
  start: number;
  end: number;
  speakerConfidence?: number;
}

/** A speaker plus the highest-confidence sentences attributed to them. */
export interface SpeakerSample {
  /** The raw speaker label from the transcript (what a rename targets). */
  label: string;
  /** Total number of entries currently assigned to this speaker. */
  entryCount: number;
  /** Best-representative sentences, highest speaker-confidence first. */
  sentences: SpeakerSampleSentence[];
}

/** A sentence ranks higher when the AI is more sure it belongs to its speaker. */
const scoreOf = (s: TranscriptSentence): number =>
  typeof s.speakerConfidence === "number" ? s.speakerConfidence : -1;

/** Number of whitespace-delimited words in a sentence's text. */
const wordCount = (text: string): number =>
  text.trim().split(/\s+/).filter(Boolean).length;

/**
 * For each distinct speaker in the transcript (first-appearance order), returns
 * up to `perSpeaker` sentences the AI is most confident belong to them — the
 * clearest examples of that voice, used to prompt the reviewer for a name.
 *
 * Only sentences of at least {@link MIN_SAMPLE_WORDS} words qualify — short
 * lines rarely help identify a voice. Qualifying sentences are ranked by
 * speaker-confidence, then by length (a longer line is usually more
 * identifying), then by start time; exact duplicate texts are skipped. Speakers
 * with no qualifying sentence are omitted.
 */
export const highestConfidenceSamplesBySpeaker = (
  transcript: TranscriptData | null,
  perSpeaker = 2,
): SpeakerSample[] => {
  // A non-positive cap means "no samples" — return early so the per-speaker
  // loop's post-push break can't leak one sentence through at perSpeaker <= 0.
  if (perSpeaker <= 0) return [];

  const order: string[] = [];
  const byLabel = new Map<
    string,
    { entryCount: number; sentences: TranscriptSentence[] }
  >();

  for (const p of transcript?.paragraphs ?? []) {
    const label = String(p.speaker ?? "");
    let bucket = byLabel.get(label);
    if (!bucket) {
      bucket = { entryCount: 0, sentences: [] };
      byLabel.set(label, bucket);
      order.push(label);
    }
    bucket.entryCount += 1;
    for (const s of p.sentences ?? []) {
      // Only sentences long enough to be identifying qualify as samples.
      if (typeof s.text === "string" && wordCount(s.text) >= MIN_SAMPLE_WORDS) {
        bucket.sentences.push(s);
      }
    }
  }

  const samples: SpeakerSample[] = [];
  for (const label of order) {
    const bucket = byLabel.get(label);
    if (!bucket) continue;
    const ranked = bucket.sentences
      .slice()
      .sort(
        (a, b) =>
          scoreOf(b) - scoreOf(a) ||
          b.text.trim().length - a.text.trim().length ||
          a.start - b.start,
      );

    const chosen: SpeakerSampleSentence[] = [];
    const seen = new Set<string>();
    for (const s of ranked) {
      const key = s.text.trim();
      if (seen.has(key)) continue;
      seen.add(key);
      chosen.push({
        text: s.text.trim(),
        start: s.start,
        end: s.end,
        speakerConfidence: s.speakerConfidence,
      });
      if (chosen.length >= perSpeaker) break;
    }

    if (chosen.length > 0) {
      samples.push({ label, entryCount: bucket.entryCount, sentences: chosen });
    }
  }

  return samples;
};
